import Api from '@/data/Api.ts';
import StringUtils from '@/infrastructure/support/utils/StringUtils.ts';
import type Company from '@/domain/model/business/Company.ts';

const API_URL_CREATE_PLANT = '/companies/{0}/plants';
const API_URL_DELETE_PLANT = '/plants/{0}';
const API_URL_GENERATE_METERS_REPORT = '/companies/{0}/meters/generate-report';
const API_URL_GENERATE_PLANTS_REPORT = '/companies/{0}/plants/generate-report';
const API_URL_GET_ALL_PLANTS_OF_COMPANY = '/companies/{0}/plants';
const API_URL_GET_ALL_PLANT_ISSUES_OF_COMPANY = '/companies/{0}/plant-issues';
const API_URL_GET_ALL_PLANT_LOCATIONS = '/companies/{0}/plant-locations';
const API_URL_GET_ALL_ISSUE_LOCATIONS = '/companies/{0}/plant-issue-locations';
const API_URL_GET_ISSUES = '/companies/{0}/plants/{1}/issues';
const API_URL_GET_PLANT_OWNING_COMPANY = '/plants/{0}/owning-company';
const API_URL_GET_PROMISE_STATUSES =
  '/companies/{0}/plants/{1}/promise-statuses';
const API_URL_GET_SINGLE_PLANT = '/companies/{0}/plants/{1}';

const API_URL_GET_SINGLE_PLANT_AUDITS = '/companies/{0}/plants/{1}/audits';
const API_URL_GET_SINGLE_PLANT_METERS_AUDITS =
  '/companies/{0}/plants/{1}/meters/audits';
const API_URL_GET_SINGLE_PLANT_METERS = '/companies/{0}/plants/{1}/meters';
const API_URL_GET_SINGLE_PLANT_TICKETS = '/companies/{0}/plants/{1}/tickets';
const API_URL_GET_SINGLE_PLANT_TAGS = '/companies/{0}/plants/{1}/tags';
const API_URL_GET_SINGLE_PLANT_COMPANIES =
  '/companies/{0}/plants/{1}/companies';
const API_URL_UPDATE_PLANT = '/companies/{0}/plants/{1}';

const API_URL_PLANT_COMPANIES = '/companies/{0}/plants/{1}/companies/{2}';

export default class PlantApi extends Api {
  /**
   * Get all plants of a company.
   *
   * @param companyId The associated company id.
   * @param params The parameters to send with the request.
   * @param includes An array of relations to include with the entity e.g. ['tags', 'companies'].
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  getAllPlantsOfCompany(
    companyId: number,
    params: {} = {},
    includes = [],
  ): Promise<any> {
    const url: string = StringUtils.stringFormat(
      API_URL_GET_ALL_PLANTS_OF_COMPANY,
      [companyId],
    );
    return this.standardGet(url, params, includes);
  }

  // noinspection JSUnusedGlobalSymbols called dynamically
  getAllPlantIssuesOfCompany(
    companyId: number,
    params: {} = {},
    includes = [],
  ): Promise<any> {
    const url: string = StringUtils.stringFormat(
      API_URL_GET_ALL_PLANT_ISSUES_OF_COMPANY,
      [companyId],
    );
    return this.standardGet(url, params, includes);
  }

  getAllPlantLocations(company: Company, params: {}): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(API_URL_GET_ALL_PLANT_LOCATIONS, [
        company.getId(),
      ]),
      params,
      [],
    );
  }

  getAllIssueLocations(company: Company, params: {}): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(API_URL_GET_ALL_ISSUE_LOCATIONS, [
        company.getId(),
      ]),
      params,
      [],
    );
  }

  /**
   * Get a single plant.
   *
   * @param companyId The associated company id.
   * @param plantId The associated plant id.
   * @param params The parameters to send with the request.
   * @param includes An array of relations to include with the entity e.g. ['tags', 'companies'].
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  getSinglePlant(
    companyId: number,
    plantId: number,
    params: {} = {},
    includes = [],
  ): Promise<any> {
    const url: string = StringUtils.stringFormat(API_URL_GET_SINGLE_PLANT, [
      companyId,
      plantId,
    ]);
    return this.standardGet(url, params, includes);
  }

  getOwningCompany(plantId: number): Promise<any> {
    const url: string = StringUtils.stringFormat(
      API_URL_GET_PLANT_OWNING_COMPANY,
      [plantId],
    );
    return this.standardGet(url, [], []);
  }

  /**
   * Get the meters associated with the given plantId
   *
   * @param companyId The associated company id.
   * @param plantId The associated plant id.
   * @param includes
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  getSinglePlantMeters(
    companyId: number,
    plantId: number,
    includes = [],
  ): Promise<any> {
    let url: string = StringUtils.stringFormat(
      API_URL_GET_SINGLE_PLANT_METERS,
      [companyId, plantId],
    );
    return this.standardGet(url, {}, includes);
  }

  /**
   * Get the tickets associated with the given plantId
   *
   * @param companyId The associated company id.
   * @param plantId The associated plant id.
   * @param includes
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  getSinglePlantTickets(
    companyId: number,
    plantId: number,
    includes = [],
  ): Promise<any> {
    const url: string = StringUtils.stringFormat(
      API_URL_GET_SINGLE_PLANT_TICKETS,
      [companyId, plantId],
    );
    return this.standardGet(url, {}, includes);
  }

  /**
   * Get the audits associated with the given plantId
   *
   * @param companyId The associated company id.
   * @param plantId The associated plant id.
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  getSinglePlantAudits(companyId: number, plantId: number): Promise<any> {
    const url: string = StringUtils.stringFormat(
      API_URL_GET_SINGLE_PLANT_AUDITS,
      [companyId, plantId],
    );
    return this.standardGet(url, {}, []);
  }

  /**
   * Get the audits of the associated meters (inc deleted) with the given plantId
   *
   * @param companyId The associated company id.
   * @param plantId The associated plant id.
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  getSinglePlantMeterAudits(companyId: number, plantId: number): Promise<any> {
    const url: string = StringUtils.stringFormat(
      API_URL_GET_SINGLE_PLANT_METERS_AUDITS,
      [companyId, plantId],
    );
    return this.standardGet(url, {}, []);
  }

  /**
   * Generate the meter report.
   *
   * @param companyId The associated company id.
   * @param params
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  generateMetersReport(companyId: number, params = {}): Promise<any> {
    const url: string = StringUtils.stringFormat(
      API_URL_GENERATE_METERS_REPORT,
      [companyId],
    );
    return this.standardGet(url, params, []);
  }

  /**
   * Generate the plant report.
   *
   * @param companyId The associated company id.
   * @param params
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  generatePlantsReport(companyId: number, params = {}): Promise<any> {
    const url: string = StringUtils.stringFormat(
      API_URL_GENERATE_PLANTS_REPORT,
      [companyId],
    );
    return this.standardGet(url, params, []);
  }

  /**
   * Create a plant.
   *
   * @param companyId The associated company id.
   * @param payload The payload to send with the request.
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  createPlant(companyId: number, payload = {}): Promise<any> {
    const url: string = StringUtils.stringFormat(API_URL_CREATE_PLANT, [
      companyId,
    ]);

    return this.standardPost(url, payload);
  }

  /**
   * Update a plant.
   *
   * @param plantId The plant id to update.
   * @param companyId The associated company id.
   * @param payload The payload to send with the request.
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  updatePlant(plantId: number, companyId: number, payload = {}): Promise<any> {
    const url: string = StringUtils.stringFormat(API_URL_UPDATE_PLANT, [
      companyId,
      plantId,
    ]);

    return this.standardPut(url, payload);
  }

  /**
   * Delete a plant.
   *
   * @param plantId
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  deletePlant(plantId: number): Promise<any> {
    const url: string = StringUtils.stringFormat(API_URL_DELETE_PLANT, [
      plantId,
    ]);
    return this.standardDelete(url, {});
  }

  getPromiseStatuses(companyId: number, plantId: number): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(API_URL_GET_PROMISE_STATUSES, [
        companyId,
        plantId,
      ]),
      {},
      [],
    );
  }

  getIssues(companyId: number, plantId: number, includes = []): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(API_URL_GET_ISSUES, [companyId, plantId]),
      {},
      includes,
    );
  }

  getTags(companyId: number, plantId: number): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(API_URL_GET_SINGLE_PLANT_TAGS, [
        companyId,
        plantId,
      ]),
      {},
      [],
    );
  }

  getCompanies(companyId: number, plantId: number): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(API_URL_GET_SINGLE_PLANT_COMPANIES, [
        companyId,
        plantId,
      ]),
      {},
      [],
    );
  }

  attachCompany(
    companyId: number,
    plantId: number,
    childCompanyId: number,
  ): Promise<any> {
    const url: string = StringUtils.stringFormat(API_URL_PLANT_COMPANIES, [
      companyId,
      plantId,
      childCompanyId,
    ]);

    return this.standardPut(url, {});
  }

  detachCompany(
    companyId: number,
    plantId: number,
    childCompanyId: number,
  ): Promise<any> {
    const url: string = StringUtils.stringFormat(API_URL_PLANT_COMPANIES, [
      companyId,
      plantId,
      childCompanyId,
    ]);

    return this.standardDelete(url);
  }
}
